"use client";

import type React from "react";
import { Box, TextField, Typography, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import type { MigrationFormData } from "../../../types/migrations";
import { FileUploader } from "./FileUploader";
import { useEffect, useState } from "react";

interface SetupFormProps {
  formData: MigrationFormData;
  formErrors: Partial<MigrationFormData>;
  formSubmitted: boolean;
  file: File | null;
  fileUploadStatus: "idle" | "uploading" | "success" | "error";
  uploadProgress: number;
  onChange: (e: React.ChangeEvent<{ name?: string; value: unknown }> | any) => void;
  onFileUpload: (event: React.DragEvent<HTMLDivElement> | React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: () => void;
}

/**
 * Component for the initial migration setup form
 */
export const SetupForm = ({
  formData,
  formErrors,
  formSubmitted,
  file,
  fileUploadStatus,
  uploadProgress,
  onChange,
  onFileUpload,
  onRemoveFile,
}: SetupFormProps) => {
  const [dataModel, setDataModel] = useState<any>([]);
  const [dataLLM, setDataLLM] = useState<any>([]);
  const [listRegion, setListRegion] = useState<string[]>([]);
  const [selectedRegion, setSelectRegion] = useState<any>([]);
  const [temperature, setTemperature] = useState<any>("");
  const [minTemp, setMinTemp] = useState<any>("");
  const [maxTemp, setMaxTemp] = useState<any>("");
  const [stepTemp, setStepTemp] = useState("");
  const [sourceSystemList, setSourceSystemList] = useState<any>([]);
  const [destinationSystemList, setDestinationSystemList] = useState<any>([]);
  const [projectName, setProjectName] = useState<any>("");
  const [sourceSystem, setSourceSytem] = useState<any>("");
  const [destinationSystem, setDestinationSystem] = useState<any>("");

  const dataConfigStr: any = localStorage.getItem("configuration");

  // Use effect to get data from localstorage
  useEffect(() => {
    const getDataLLM = JSON.parse(dataConfigStr);
    setDataLLM(getDataLLM.content.llm_providers);
    setDestinationSystemList(getDataLLM?.content?.proxy_managers_target!);
    setSourceSystemList(getDataLLM?.content?.proxy_managers_source!);
  }, [dataConfigStr]);

  // Use effect to set default values.
  useEffect(() => {
    formData.sourceSystem = sourceSystemList?.[0]?.id || "";
    formData.destinationSystem = destinationSystemList?.[0]?.id || "";
    let getLLMModel: any[] = [];
    if (formData.llmModel === "gemini" || formData.llmModel === "openai") {
      getLLMModel = dataLLM.find((item: any) => item.id === formData.llmModel)?.models || [];
      setDataModel(getLLMModel);
    }
    const getModel = getLLMModel.find((item: any) => item.isDefault) || getLLMModel[0];
    const region = getModel?.available_regions?.sort()[0] || "";
    const temperature = getModel?.temperature_available?.defaultValue || "";

    setListRegion(getModel?.available_regions || []);
    setSelectRegion(region);
    setTemperature(temperature);
    setMinTemp(getModel?.temperature_available?.min || "");
    setMaxTemp(getModel?.temperature_available?.max || "");
    setStepTemp(getModel?.temperature_available?.step || "");

    formData.versionModel = getModel?.id || "";
    formData.temperature = temperature;
    formData.region = region;
  }, [formData, dataLLM, destinationSystemList, sourceSystemList]);

  /**
   * Funcion para validar los campos dinamicos de acuerdo a lo que se seleccione.
   * @param e => valor del input o campo seleccionado
   */
  const handleModelChange = (e: any) => {
    const modelId = e.target.value;
    formData.versionModel = modelId;
    const model = dataLLM.find((llm: any) => llm.models.some((model: any) => model.id === modelId));
    const getModel = model.models.find((model: any) => model.id === modelId);
    const regions: any = Object.values(getModel?.available_regions).sort();

    setListRegion(regions);
    setTemperature(getModel?.temperature_available?.defaultValue || "");
    setMinTemp(getModel?.temperature_available?.min || "");
    setMaxTemp(getModel?.temperature_available?.max || "");
    setStepTemp(getModel?.temperature_available?.step || "");
    setSelectRegion(regions[0]);

    formData.region = regions[0];
  };

  // Funcion para validar no anotar numeros menor al permitido
  const handleBlur = () => {
    const num = parseFloat(temperature);
    if (isNaN(num) || num < minTemp) {
      setTemperature(minTemp);
    } else if (num > maxTemp) {
      setTemperature(maxTemp);
    }
    setTemperature(num);
    formData.temperature = num;
  };

  // Funcion para validar no anotar numeros menor al permitido
  const handleChangeTemperature = (e: any) => {
    const input = e.target.value;
    const num = parseFloat(input);
    if (input === "" || (!isNaN(num) && num >= minTemp && num <= maxTemp)) {
      setTemperature(input);
    }
  };

  /**
   * Funcion para setear region
   * @param e Evento del campo
   */
  const handleChangeregion = (e: any) => {
    const inputRegion = e.target.value;
    setSelectRegion(inputRegion);
    formData.region = inputRegion;
  };

  /**
   * Funcion para setear Source System
   * @param e Evento del campo
   */
  const handleChangeSourceSystem = (e: any) => {
    const inputSoureSystem = e.target.value;
    const getSourceSystem = sourceSystemList.find((source: any) => source.id === inputSoureSystem);
    formData.sourceSystem = getSourceSystem.id;
    setSourceSytem(formData.sourceSystem);
  };

  /**
   * Funcion para setear Destination System
   * @param e Evento del campo
   */
  const handleChangeDestinationSystem = (e: any) => {
    const inputDestinationSystem = e.target.value;
    const getDestinationSystem = destinationSystemList.find(
      (destination: any) => destination.id === inputDestinationSystem
    );
    formData.sourceSystem = getDestinationSystem.id;
    setDestinationSystem(formData.sourceSystem);
  };

  /**
   * Funcion para setear nombre y no cambiar logica del formulario
   * @param e Evento del campo
   */
  const handleName = (e: any) => {
    const inputName = e.target.value;
    setProjectName(inputName);
    formData.projectName = inputName;
  };

  return (
    <>
      <Typography variant="h6" sx={{ mb: 3, color: "#414141" }}>
        Initial Setup
      </Typography>

      <Box sx={{ display: "flex", gap: 2, mb: 4 }}>
        <TextField
          name="projectName"
          label="Migration name"
          placeholder="Enter a name"
          value={projectName}
          onChange={handleName}
          fullWidth
          required
          error={formSubmitted && !!formErrors.projectName}
          helperText={formSubmitted && formErrors.projectName}
          inputProps={{ maxLength: 50 }}
        />
        <FormControl fullWidth required error={formSubmitted && !!formErrors.llmModel}>
          <InputLabel id="llm-select-label">LLM</InputLabel>
          <Select labelId="llm-select-label" name="llmModel" value={formData.llmModel} label="LLM" onChange={onChange}>
            {dataLLM?.map((llmModel: any) => (
              <MenuItem key={llmModel.id} value={llmModel.id}>
                {llmModel.name}
              </MenuItem>
            ))}
          </Select>
          {formSubmitted && formErrors.llmModel && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
              {formErrors.llmModel}
            </Typography>
          )}
        </FormControl>
      </Box>
      <Box sx={{ display: "flex", gap: 3, mb: 4 }}>
        {formData.llmModel !== "" && (
          <FormControl fullWidth required error={formSubmitted && !!formErrors.versionModel}>
            <InputLabel id="version-select-label">Model</InputLabel>
            <Select
              labelId="version-select-label"
              name="version"
              value={formData.versionModel}
              label="Version"
              onChange={handleModelChange}
            >
              {dataModel?.map((model: any) => (
                <MenuItem key={model.id} value={model.id}>
                  {model.name}
                </MenuItem>
              ))}
            </Select>
            {formSubmitted && formErrors.versionModel && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                {formErrors.versionModel}
              </Typography>
            )}
          </FormControl>
        )}
        <FormControl fullWidth required error={formSubmitted && !!formErrors.region}>
          <InputLabel id="llm-select-label">Region</InputLabel>
          <Select
            labelId="llm-select-label"
            name="region"
            value={Array.isArray(listRegion) && listRegion.includes(selectedRegion) ? selectedRegion : ""}
            label="Region"
            onChange={handleChangeregion}
          >
            {listRegion?.map((region: any) => (
              <MenuItem key={region} value={region}>
                {region}
              </MenuItem>
            ))}
          </Select>
          {formSubmitted && formErrors.region && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
              {formErrors.region}
            </Typography>
          )}
        </FormControl>
        <FormControl fullWidth required error={formSubmitted && !!formErrors.temperature}>
          <TextField
            label="Temperatura"
            type="number"
            inputProps={{
              min: parseFloat(minTemp),
              max: parseFloat(maxTemp),
              step: parseFloat(stepTemp),
            }}
            value={temperature}
            onChange={handleChangeTemperature}
            onBlur={handleBlur}
          />
        </FormControl>
        {formSubmitted && formErrors.temperature && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
            {formErrors.temperature}
          </Typography>
        )}
      </Box>
      <Typography sx={{ mb: 2, color: "#666666" }}>
        For the Migration, select first the source system, and then the destination system.
      </Typography>
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 4 }}>
        <FormControl fullWidth required error={formSubmitted && !!formErrors.sourceSystem}>
          <InputLabel>Source System</InputLabel>
          <Select
            name="sourceSystem"
            value={sourceSystem ? sourceSystem : formData.sourceSystem}
            label="Source System"
            onChange={handleChangeSourceSystem}
          >
            {sourceSystemList?.map((source: any) => (
              <MenuItem key={source.id} value={source.id}>
                {source.name}
              </MenuItem>
            ))}
          </Select>
          {formSubmitted && formErrors.sourceSystem && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
              {formErrors.sourceSystem}
            </Typography>
          )}
        </FormControl>
        <Box sx={{ px: 2 }}>→</Box>
        <FormControl fullWidth required error={formSubmitted && !!formErrors.destinationSystem}>
          <InputLabel>Destination System</InputLabel>
          <Select
            name="destinationSystem"
            value={destinationSystem ? destinationSystem : formData.destinationSystem}
            label="Destination System"
            onChange={handleChangeDestinationSystem}
          >
            {destinationSystemList?.map((destination: any) => (
              <MenuItem key={destination.id} value={destination.id}>
                {destination.name}
              </MenuItem>
            ))}
          </Select>
          {formSubmitted && formErrors.destinationSystem && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
              {formErrors.destinationSystem}
            </Typography>
          )}
        </FormControl>
      </Box>
      <Typography sx={{ mb: 2, color: "#666666" }}>
        Select the file that will be used <span style={{ color: "red" }}>*</span>
      </Typography>
      <FileUploader
        file={file}
        fileUploadStatus={fileUploadStatus}
        uploadProgress={uploadProgress}
        formSubmitted={formSubmitted}
        onFileUpload={onFileUpload}
        onRemoveFile={onRemoveFile}
      />
    </>
  );
};
